/**
 * components/DarkModeToggle.js — Phase 3
 *
 * Animated sun/moon toggle for the dashboard top navigation bar.
 * Uses MUI IconButton + Tooltip so it blends with the existing navbar.
 *
 * Place it in the right side of your AppBar / Toolbar, e.g.:
 *   import DarkModeToggle from '../components/DarkModeToggle';
 *   // inside your Toolbar:
 *   <DarkModeToggle />
 */

import React from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { LightMode, DarkMode } from '@mui/icons-material';
import { useThemeMode } from '../context/ThemeContext';

export default function DarkModeToggle({ size = 'medium' }) {
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === 'dark';

  return (
    <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} placement="bottom">
      <IconButton
        onClick={toggleMode}
        size={size}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-pressed={isDark}
        sx={{
          position: 'relative',
          width:  36,
          height: 36,
          borderRadius: '10px',
          bgcolor: isDark ? 'rgba(66,165,245,0.12)' : 'rgba(21,101,192,0.08)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(66,165,245,0.25)' : 'rgba(21,101,192,0.15)',
          transition: 'all 250ms cubic-bezier(0.4,0,0.2,1)',
          '&:hover': {
            bgcolor: isDark ? 'rgba(66,165,245,0.2)' : 'rgba(21,101,192,0.15)',
            transform: 'scale(1.05)',
          },
        }}
      >
        {/* Sun icon fades out / Moon fades in */}
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            opacity: isDark ? 0 : 1,
            transform: isDark ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)',
            transition: 'all 300ms cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <LightMode sx={{ fontSize: 18, color: '#F57F17' }} />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            opacity: isDark ? 1 : 0,
            transform: isDark ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)',
            transition: 'all 300ms cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <DarkMode sx={{ fontSize: 18, color: '#90CAF9' }} />
        </Box>
      </IconButton>
    </Tooltip>
  );
}
